<template>
  <footer class="relative w-full bg-burse-maroon py-12" style="z-index: 10">
    <ResponsiveWrapper>
      <div class="w-full flex flex-col gap-10">
        <!-- Top section: brand + links -->
        <div class="flex flex-col gap-8 md:flex-row md:gap-25 md:items-start">
          <!-- Brand: logo / copyright / social -->
          <div class="flex flex-col gap-2 text-white shrink-0">
            <a href="/">
              <img
                :src="burseLogoWhite"
                alt="Burse Logo"
                class="h-10 w-auto object-contain"
              />
            </a>
            <div class="flex flex-col gap-6 mt-4">
              <div class="flex flex-col gap-2 text-sm leading-5">
                <p>Copyright © {{ currentYear }} Burse</p>
                <p>All rights reserved</p>
              </div>
              <p class="text-sm leading-5">Banking Services by Ure MFB</p>
              <div class="flex items-center gap-4">
                <a
                  href="https://www.instagram.com/useburse/"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Instagram"
                  class="transition-opacity hover:opacity-80"
                >
                  <img
                    :src="instagramIcon"
                    alt="Instagram"
                    class="w-8 h-8 object-contain"
                  />
                </a>
                <a
                  href="https://x.com/useburse"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Twitter"
                  class="transition-opacity hover:opacity-80"
                >
                  <img
                    :src="twitterIcon"
                    alt="Twitter"
                    class="w-8 h-8 object-contain"
                  />
                </a>
                <a
                  href="https://www.facebook.com/profile.php?id=61567627372144"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Facebook"
                  class="transition-opacity hover:opacity-80"
                >
                  <img
                    :src="facebookIcon"
                    alt="Facebook"
                    class="w-6 h-6 object-contain"
                  />
                </a>
                <a
                  href="https://www.linkedin.com/company/useburse/"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="LinkedIn"
                  class="transition-opacity hover:opacity-80"
                >
                  <img
                    :src="linkedinIcon"
                    alt="LinkedIn"
                    class="w-8 h-8 object-contain"
                  />
                </a>
              </div>
            </div>
          </div>

          <!-- Link columns -->
          <div class="flex-1 text-white">
            <!-- Mobile: 2-col grid for main link groups -->
            <div class="grid grid-cols-2 gap-8 md:hidden">
              <div
                v-for="col in linkColumns"
                :key="col.title"
                class="flex flex-col gap-4"
              >
                <h3 class="text-base font-semibold tracking-footer-title">
                  {{ col.title }}
                </h3>
                <ul class="flex flex-col gap-3 text-sm text-white/95">
                  <li v-for="link in col.links" :key="link.label">
                    <a
                      v-if="link.href"
                      :href="link.href"
                      class="transition-opacity hover:opacity-80 leading-snug"
                      :target="link.external ? '_blank' : undefined"
                      :rel="link.external ? 'noopener noreferrer' : undefined"
                      >{{ link.label }}</a
                    >
                    <span v-else class="leading-snug">{{ link.label }}</span>
                  </li>
                </ul>
              </div>
            </div>

            <!-- Desktop: all columns in a flex row -->
            <div class="hidden md:flex gap-4">
              <div
                v-for="col in allColumns"
                :key="col.title"
                class="flex-1 flex flex-col gap-4"
              >
                <h3 class="text-base font-semibold tracking-footer-title">
                  {{ col.title }}
                </h3>
                <ul class="flex flex-col gap-3 text-sm text-white/95">
                  <li v-for="link in col.links" :key="link.label">
                    <a
                      v-if="link.href"
                      :href="link.href"
                      class="transition-opacity hover:opacity-80 leading-snug"
                      :target="link.external ? '_blank' : undefined"
                      :rel="link.external ? 'noopener noreferrer' : undefined"
                      >{{ link.label }}</a
                    >
                    <span v-else class="leading-snug">{{ link.label }}</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <!-- Contact — mobile only (desktop shows it inside the columns flex row) -->
        <div class="flex flex-col gap-4 text-white md:hidden">
          <h3 class="text-base font-semibold tracking-footer-title">
            {{ contactColumn.title }}
          </h3>
          <ul class="flex flex-col gap-3 text-sm text-white/95">
            <li v-for="link in contactColumn.links" :key="link.label">
              <a
                v-if="link.href"
                :href="link.href"
                class="transition-opacity hover:opacity-80 leading-snug"
                >{{ link.label }}</a
              >
              <span v-else class="leading-snug">{{ link.label }}</span>
            </li>
          </ul>
        </div>

        <!-- Disclaimer -->
        <div class="flex flex-col gap-3 text-sm leading-relaxed text-white/95">
          <p>
            Burse Services Limited is redefining personal wealth management. We
            give individuals the digital tools to save, invest, and automate
            their finances, putting the power of smart money management in the
            palm of their hand.
          </p>
          <p>
            Mutual Funds Investment services are provided by our partner, ARM
            Investment Managers, and are made accessible to our clients
            through our mobile application.
          </p>
        </div>
      </div>
    </ResponsiveWrapper>
  </footer>
</template>

<script setup>
import burseLogoWhite from "../assets/burse-logo.svg?url";
import instagramIcon from "../assets/social/instagram.svg?url";
import twitterIcon from "../assets/social/twitter.svg?url";
import facebookIcon from "../assets/social/facebook.svg?url";
import linkedinIcon from "../assets/social/linkedin.svg?url";
import ResponsiveWrapper from "./ResponsiveWrapper.vue";

const currentYear = new Date().getFullYear();

const linkColumns = [
  {
    title: "Company",
    links: [
      { label: "About us", href: "/about-us" },
    ],
  },
  {
    title: "Resources",
    links: [{ label: "Wealth Calculator", href: "/burse/calculator" }],
  },
  {
    title: "Savings",
    links: [
      { label: "Build Wealth", href: "/burse/buildwealth" },
      { label: "Burse Vault", href: "/burse/bursevault" },
      { label: "Burse Flex", href: "/burse/burseflex" },
    ],
  },
  {
    title: "Legal",
    links: [
      { label: "Privacy Policy", href: "/burse/privacy-policy" },
      { label: "Terms of Service", href: "/burse/terms-of-use" },
    ],
  },
];

const contactColumn = {
  title: "Contact",
  links: [
    { label: "hi@useburse.com", href: "mailto:hi@useburse.com" },
    { label: "+234 201 330 9528", href: "tel:+2342013309528" },
    { label: "8B, FibreOne Broadband St. (formerly Ayanboye St), Anthony, Lagos, Nigeria." },
  ],
};

const allColumns = [...linkColumns, contactColumn];
</script>
